import React, { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Calendar } from "components/ui/calendar";
import { cn } from "lib/utils";

interface DatePickerProps {
  date?: Date;
  setDate: (date: Date | undefined) => void;
  placeholder: string;
  minDate?: Date;
  maxDate?: Date;
  error?: boolean;
}

const DatePicker = ({
  date,
  setDate,
  placeholder,
  minDate,
  maxDate,
  error,
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);

  // Create disabled matcher function for react-day-picker
  const disabledDays = (day: Date) => {
    if (minDate && day < minDate) return true;
    if (maxDate && day > maxDate) return true;
    return false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-8 w-40 justify-start text-left font-normal text-xs",
            !date && "text-muted-foreground",
            error && "border-red-500 focus-visible:ring-red-500"
          )}
        >
          <CalendarIcon className="mr-2 h-3 w-3" />
          {date ? format(date, "MMM dd, yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          key={placeholder} // Unique key to isolate calendar instances
          mode="single"
          selected={date}
          defaultMonth={date || new Date()} // Show selected date's month or current month
          disabled={disabledDays}
          onSelect={(selectedDate) => {
            setDate(selectedDate);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
